// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { Button, Flex, Typography } from '@iheartjane/reefer';
import { spacing } from '@iheartjane/reefer-emotion';
import dompurify from 'dompurify';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';

import { Modal } from '@jane/kiosk/components';
import type { RootLoaderType } from '@jane/kiosk/data-access';
import { useKioskSpacing, useTypedRouteLoaderData } from '@jane/kiosk/hooks';
import { useMenu } from '@jane/kiosk/providers';
import type { MenuCategory } from '@jane/kiosk/util';

const StyledModalContent = styled(Modal.Content)({
  ...spacing({ p: 24 }),
  // These styles are for the inserted HTML to properly match the HTML editor from the business dashboard.
  '.ql-size-large': {
    fontFamily: 'inherit',
    fontSize: '1.5em',
  },
  '.ql-size-small': {
    fontFamily: 'inherit',
    fontSize: '0.75em',
  },
  'ul, ol': {
    paddingLeft: '1.5em',
  },
  'ul li': {
    listStyle: 'disc inside',
    paddingLeft: 0,
  },
  'ol li': {
    listStyle: 'decimal inside',
  },
  a: {
    color: 'blue',
    textDecoration: 'underline',
    '&:hover': {
      color: 'darkBlue',
    },
  },
  's, del': {
    textDecoration: 'line-through',
  },
});

export const CommunicationBannerModal = () => {
  const { storeId } = useTypedRouteLoaderData<RootLoaderType>('root');
  const { bannerMessage } = useMenu();
  const { category = 'all' } = useParams<{ category: MenuCategory }>();

  const navigate = useNavigate();

  const onClose = useCallback(() => {
    navigate(`/stores/${storeId}/menu/${category}`);
  }, [category, navigate, storeId]);

  const { getKioskSpacing, defaultSpacing } = useKioskSpacing();

  return bannerMessage?.enabled &&
    (bannerMessage.title || bannerMessage.message) ? (
    <Flex width="100%">
      <Modal open={true} onRequestClose={onClose} variant="dialogue">
        <StyledModalContent>
          {bannerMessage.title && (
            <Typography mb={getKioskSpacing(24, 8)} variant="h2-semibold">
              {bannerMessage.title}
            </Typography>
          )}
          {bannerMessage.message && (
            <div
              dangerouslySetInnerHTML={{
                __html: dompurify.sanitize(bannerMessage.message),
              }}
            />
          )}
        </StyledModalContent>
        <Modal.Footer>
          <Button
            ariaLabel="close and start shopping"
            full
            mt={defaultSpacing}
            label="Start shopping"
            onClick={onClose}
          />
        </Modal.Footer>
      </Modal>
    </Flex>
  ) : null;
};
